import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  RefreshCw,
  Search,
  Download,
  CalendarDays,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { adminAPI } from '@/services/api';
import {
  StatusBadge,
  formatDate,
  formatDateTime,
  serviceLabel,
  downloadBlob,
} from './adminUtils';

const STATUSES = ['all', 'pending', 'confirmed', 'completed', 'cancelled', 'replied'];

export default function AppointmentsPanel() {
  const { token } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notes, setNotes] = useState('');
  const [reply, setReply] = useState('');
  const [editStatus, setEditStatus] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const list = await adminAPI.listAppointments(token, params);
      setItems(list);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [token, status, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase().trim();
    return items.filter((a) => {
      const c = a.customer || {};
      const hay = [c.name, c.email, c.phone, a.id, a.status, a.appointment?.service_type]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const selected = filtered.find((a) => a.id === selectedId) || items.find((a) => a.id === selectedId);

  useEffect(() => {
    if (selected) {
      setNotes(selected.admin_notes || '');
      setReply(selected.admin_reply || '');
      setEditStatus(selected.status || 'pending');
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async ({ sendEmail = false } = {}) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminAPI.updateAppointment(token, selected.id, {
        status: editStatus,
        admin_notes: notes,
        admin_reply: reply,
        send_reply_email: sendEmail,
      });
      setItems((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to update appointment');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = status !== 'all' ? { status } : {};
      const blob = await adminAPI.exportAppointmentsCsv(token, params);
      downloadBlob(blob, 'appointments.csv');
    } catch (err) {
      setError(err?.detail || err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {filtered.length} shown · filter, update status, add notes, or email a reply
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || items.length === 0}>
            {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
            Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="relative xl:col-span-1 sm:col-span-2">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone…"
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid xl:grid-cols-5 gap-4">
        <div className="xl:col-span-3 bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-brand-blue-500" size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <CalendarDays className="mx-auto text-gray-300 mb-3" size={36} />
              <p className="text-sm text-gray-500">No appointments found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/80 text-left text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Service</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((a) => {
                    const active = a.id === selectedId;
                    return (
                      <tr
                        key={a.id}
                        onClick={() => setSelectedId(a.id)}
                        className={`cursor-pointer transition-colors ${active ? 'bg-brand-blue-50/60' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{a.customer?.name}</p>
                          <p className="text-xs text-gray-400">{a.customer?.email}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="text-gray-700">{formatDate(a.appointment?.date)}</p>
                          <p className="text-xs text-gray-400">{a.appointment?.time}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{serviceLabel(a.appointment?.service_type)}</td>
                        <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="xl:col-span-2 bg-white border border-gray-100 rounded-xl shadow-sm p-5">
          {!selected ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-gray-400">
              Select an appointment to view details
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selected.customer?.name}</h2>
                <p className="text-sm text-gray-500">{selected.customer?.email} · {selected.customer?.phone}</p>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Date</dt>
                  <dd className="font-medium text-gray-800">{formatDate(selected.appointment?.date)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Time</dt>
                  <dd className="font-medium text-gray-800">{selected.appointment?.time}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Type</dt>
                  <dd className="font-medium text-gray-800 capitalize">{selected.appointment?.appointment_type?.replace('_', ' ')}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400 uppercase">Service</dt>
                  <dd className="font-medium text-gray-800">{serviceLabel(selected.appointment?.service_type)}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs text-gray-400 uppercase">Customer notes</dt>
                  <dd className="text-gray-700 mt-0.5">{selected.appointment?.notes || '-'}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs text-gray-400 uppercase">Created</dt>
                  <dd className="text-gray-600">{formatDateTime(selected.created_at)}</dd>
                </div>
              </dl>

              <div>
                <Label>Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.filter((s) => s !== 'all').map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="admin-notes">Internal notes</Label>
                <Textarea
                  id="admin-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-1.5"
                  placeholder="Private staff notes…"
                />
              </div>

              <div>
                <Label htmlFor="admin-reply">Reply to customer</Label>
                <Textarea
                  id="admin-reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  className="mt-1.5"
                  placeholder="Optional email reply body…"
                />
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  onClick={() => handleSave({ sendEmail: false })}
                  disabled={saving}
                  className="bg-brand-blue-500 hover:bg-brand-blue-600 text-white"
                >
                  {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSave({ sendEmail: true })}
                  disabled={saving || !reply.trim()}
                  className="border-brand-gold-300 text-brand-gold-800 hover:bg-brand-gold-50"
                >
                  Save & email reply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
